import { formatInTimeZone, fromZonedTime } from "date-fns-tz";

import {
  DAILY_TODO_SECTIONS,
  DailyTodoCardI,
  DailyTodoSectionKeyT,
  GetDailyTodosResponseI,
} from "@/api/routes/daily-todos";
import Router from "@/helpers/router";

/**
 * Times and the daily gate for Leads → To-Dos (C-16 S6). ET is the business
 * clock (the day, the snooze limit); what the caller reads is in their own
 * time zone. The server stores and checks every rule; these mirror them so
 * the UI only offers what the server will accept.
 */

export const ET_TIMEZONE = "America/New_York";

const MINUTE = 60_000;

/** The last snooze time and the latest daily time, 9:00 PM. */
const LAST_HOUR = 21;
const FIRST_DAILY_HOUR = 7;

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * The snooze times still open today: ET half hours at least 30 minutes ahead,
 * up to 9:00 PM ET (backend `dailyTodoSnoozeSlots`).
 */
export const getSnoozeSlots = (now: Date): Array<Date> => {
  const day = formatInTimeZone(now, ET_TIMEZONE, "yyyy-MM-dd");
  const earliest = now.getTime() + 30 * MINUTE;
  const slots: Array<Date> = [];

  for (let minutes = 0; minutes <= LAST_HOUR * 60; minutes += 30) {
    const at = fromZonedTime(
      `${day}T${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}:00`,
      ET_TIMEZONE
    );

    if (at.getTime() >= earliest) {
      slots.push(at);
    }
  }

  return slots;
};

/** "9:00 AM" for "09:00". */
export const formatDailyTime = (hhmm: string): string => {
  const [hours, minutes] = hhmm.split(":").map(Number);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return "—";
  }

  const suffix = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;

  return `${hour12}:${pad(minutes)} ${suffix}`;
};

/** The daily times the server accepts: 7:00 AM to 9:00 PM on the half hour. */
export const DAILY_TIME_OPTIONS: Array<{ value: string; label: string }> =
  Array.from({ length: (LAST_HOUR - FIRST_DAILY_HOUR) * 2 + 1 }, (_, i) => {
    const minutes = FIRST_DAILY_HOUR * 60 + i * 30;
    const value = `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;

    return { value, label: formatDailyTime(value) };
  });

/**
 * The daily times to offer in `timeZone`: those that fall on the same ET day
 * (the server builds the reminder on the ET date, so 9:00 PM Pacific would be
 * the next ET day's midnight and never fire).
 */
export const getDailyTimeOptions = (
  timeZone: string,
  now = new Date()
): Array<{ value: string; label: string }> => {
  const etDay = formatInTimeZone(now, ET_TIMEZONE, "yyyy-MM-dd");

  try {
    return DAILY_TIME_OPTIONS.filter(
      (option) =>
        formatInTimeZone(
          fromZonedTime(`${etDay}T${option.value}:00`, timeZone),
          ET_TIMEZONE,
          "yyyy-MM-dd"
        ) === etDay
    );
  } catch {
    return DAILY_TIME_OPTIONS;
  }
};

/** The intro's default: 9:00 AM, or the first time the zone can have. */
export const getDefaultDailyTime = (
  options: Array<{ value: string }>
): string =>
  options.find((option) => option.value >= "09:00")?.value ??
  options[0]?.value ??
  "09:00";

/** The browser's IANA zone, for the daily time; ET when unknown. */
export const getBrowserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ET_TIMEZONE;
  } catch {
    return ET_TIMEZONE;
  }
};

/** "CT" style abbreviation of a zone, for labels. */
export const getTimeZoneLabel = (timeZone: string, now = new Date()) => {
  try {
    return formatInTimeZone(now, timeZone, "zzz");
  } catch {
    return "";
  }
};

/** "2:30 PM" in the viewer's zone. */
export const formatClock = (at: Date | string, timeZone?: string): string => {
  const date = typeof at === "string" ? new Date(at) : at;

  return formatInTimeZone(date, timeZone ?? getBrowserTimeZone(), "h:mm a");
};

/** "Oct 20" in the viewer's zone. */
export const formatShortDate = (
  at: Date | string,
  timeZone?: string
): string => {
  const date = typeof at === "string" ? new Date(at) : at;

  return formatInTimeZone(date, timeZone ?? getBrowserTimeZone(), "MMM d");
};

export const getGreeting = (now: Date, timeZone?: string): string => {
  const hour = Number(
    formatInTimeZone(now, timeZone ?? getBrowserTimeZone(), "H")
  );

  if (hour < 12) {
    return "Good morning";
  }

  if (hour < 17) {
    return "Good afternoon";
  }

  return "Good evening";
};

export const SECTION_COPY: Record<
  DailyTodoSectionKeyT,
  { title: string; subtitle: string }
> = {
  [DAILY_TODO_SECTIONS.TODAY]: {
    title: "Today's meetings",
    subtitle: "Confirm they're still on, so they show.",
  },
  [DAILY_TODO_SECTIONS.TOMORROW]: {
    title: "Tomorrow's meetings",
    subtitle: "Touch base today, so tomorrow's meeting holds.",
  },
  [DAILY_TODO_SECTIONS.NO_SHOW]: {
    title: "No-shows and cancels",
    subtitle: "They booked once. Follow up and rebook.",
  },
};

/** "2 meetings today, 1 tomorrow and 4 no-shows or cancels". */
export const describeTodoCounts = (
  todos: Pick<GetDailyTodosResponseI, "sections">
): string => {
  const count = (key: DailyTodoSectionKeyT) =>
    todos.sections.find((section) => section.key === key)?.total ?? 0;

  const parts: Array<string> = [];
  const today = count(DAILY_TODO_SECTIONS.TODAY);
  const tomorrow = count(DAILY_TODO_SECTIONS.TOMORROW);
  const noShows = count(DAILY_TODO_SECTIONS.NO_SHOW);

  if (today > 0) {
    parts.push(`${today} ${today === 1 ? "meeting" : "meetings"} today`);
  }

  if (tomorrow > 0) {
    parts.push(
      `${tomorrow} ${tomorrow === 1 ? "meeting" : "meetings"} tomorrow`
    );
  }

  if (noShows > 0) {
    parts.push(
      `${noShows} ${noShows === 1 ? "no-show or cancel" : "no-shows or cancels"}`
    );
  }

  if (parts.length === 0) {
    return "nothing to work";
  }

  if (parts.length === 1) {
    return parts[0];
  }

  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
};

export const GATE_LAYERS = {
  NONE: "none",
  INTRO: "intro",
  BRIEFING: "briefing",
  REMINDER: "reminder",
  MUST_FINISH: "must_finish",
} as const;

export type GateLayerT = (typeof GATE_LAYERS)[keyof typeof GATE_LAYERS];

/** Pages where the gate never opens: the list itself and the dialer. */
export const isQuietRoute = (pathname: string): boolean =>
  pathname === Router.todos() ||
  pathname === Router.session() ||
  pathname.startsWith(`${Router.session()}/`);

export interface GateInputI {
  todos: GetDailyTodosResponseI | null;
  now: Date;
  pathname: string;
  /** A call may be live: a dialing session, or an open dialer or account panel. */
  isBusy: boolean;
  /** The briefing was closed on this page load. */
  isBriefingClosed: boolean;
  /** The intro couldn't be saved and the caller chose "Later" (this page load). */
  isIntroDeferred?: boolean;
}

/**
 * Which layer of the daily gate to show (C16-D474), one at a time:
 * 1. the one-time intro (set the daily time);
 * 2. must-finish, once the day's snooze ran out with cards left (only
 *    "Let's go", back on every page until the list is done);
 * 3. the reminder, from the daily time (and an hour after the last dial
 *    block) until the caller snoozes, finishes or may stop;
 * 4. the briefing, once on the day's first visit.
 * Nothing opens on the To-Dos page, in the dialer, while a call may be live,
 * on a day with nothing to work, or once the list is done or stopped for the
 * day.
 */
export const getGateLayer = ({
  todos,
  now,
  pathname,
  isBusy,
  isBriefingClosed,
  isIntroDeferred = false,
}: GateInputI): GateLayerT => {
  if (!todos || isBusy || isQuietRoute(pathname)) {
    return GATE_LAYERS.NONE;
  }

  const { state, settings } = todos;

  if (state.total === 0 || state.complete || state.dismissed) {
    return GATE_LAYERS.NONE;
  }

  if (!settings.intro_done && !isIntroDeferred) {
    return GATE_LAYERS.INTRO;
  }

  const snoozedUntil = state.snoozed_until
    ? new Date(state.snoozed_until)
    : null;

  if (state.must_finish || (snoozedUntil && now >= snoozedUntil)) {
    return GATE_LAYERS.MUST_FINISH;
  }

  if (now >= new Date(state.reminder_at) && !snoozedUntil) {
    return GATE_LAYERS.REMINDER;
  }

  if (!state.briefing_seen && !isBriefingClosed && !snoozedUntil) {
    return GATE_LAYERS.BRIEFING;
  }

  return GATE_LAYERS.NONE;
};

/** Cards left to work today. */
export const getRemaining = (todos: GetDailyTodosResponseI | null): number =>
  todos ? Math.max(0, todos.state.total - todos.state.worked) : 0;

/** No-show cards grouped by campaign, campaigns A to Z (CC's grouping). */
export const groupByCampaign = (
  cards: Array<DailyTodoCardI>
): Array<{
  campaignId: string;
  campaignName: string;
  cards: Array<DailyTodoCardI>;
}> => {
  const groups = new Map<
    string,
    { campaignId: string; campaignName: string; cards: Array<DailyTodoCardI> }
  >();

  cards.forEach((card) => {
    const group = groups.get(card.campaign_id) ?? {
      campaignId: card.campaign_id,
      campaignName: card.campaign_name || "—",
      cards: [],
    };

    group.cards.push(card);
    groups.set(card.campaign_id, group);
  });

  return Array.from(groups.values()).sort((a, b) =>
    a.campaignName.localeCompare(b.campaignName)
  );
};
